"use client";

import clsx from "clsx";
import {forwardRef, useImperativeHandle} from "react";
import {useTranslation} from "react-i18next";
import {useStableCallback} from "@/hooks/useStableCallback";
import {useWingmanV2Active} from "@/hooks/useWingmanRouting";
import {Popover, PopoverAnchor} from "@/components/ui/popover";
import type {Assistant} from "../AssistantSelector";
import type {ChatInputDictationApi, ChatInputHandle, ChatInputProps, ComposerTextareaProps} from "./types";
import QueuedMessageBanner from "./QueuedMessageBanner";
import FileAttachmentsList from "./FileAttachmentsList";
import DesktopComposerLayout from "./DesktopComposerLayout";
import MobileCompactInputRow from "./MobileCompactInputRow";
import {useChatInputComposer} from "./useChatInputComposer";
import {useSlashCommandMenu} from "./useSlashCommandMenu";
import SlashCommandMenu from "./SlashCommandMenu";

export type {ChatInputHandle, ChatInputProps};

type ChatInputBaseProps = ChatInputProps & {
   dictation?: ChatInputDictationApi | null;
   inputRef: React.RefObject<HTMLTextAreaElement | null>;
};

const ChatInputBase = forwardRef<ChatInputHandle, ChatInputBaseProps>((props, ref) => {
   const {
      value,
      disabled = false,
      isSubmitting = false,
      onCancelStream,
      loading = false,
      placeholder = "",
      className,
      variant = "compact",
      showSendButton = true,
      assistants = [],
      selectedAssistant,
      onAssistantChange,
      showAssistantSelector = false,
      assistantSelectorDisabled = false,
      uploadDisabled = false,
      fileUploadConfig,
      attachedFiles = [],
      canEditModel,
      conversationId,
      onModelChange,
      isModelSaving,
      isLoadingAssistants = false,
      maxRows = 12,
      tokenLimitReached = false,
      tokenBreakdown,
      dictation = null,
      enableMemory = false,
      skills,
      isLoadingSkills = false,
      onExpandedChange,
      inputRef,
   } = props;
   const {t: ct} = useTranslation("common");
   const composer = useChatInputComposer(props);
   const slashMenu = useSlashCommandMenu(props);
   const wingmanV2Active = useWingmanV2Active(selectedAssistant?.model);
   const showMemory = enableMemory && wingmanV2Active;
   // Toolbar controls are memoized so typing does not re-render them, but
   // chat owners rebuild these handlers on every keystroke. Keep identities
   // stable and still report a missing handler as undefined.
   const stableAssistantChange = useStableCallback((assistant: Assistant) => onAssistantChange?.(assistant));
   const stableModelChange = useStableCallback((assistantId: string, model: string) => onModelChange?.(assistantId, model));
   const stableCancelStream = useStableCallback(() => onCancelStream?.());
   const stableDictationToggle = useStableCallback(() => dictation?.toggle());
   const assistantChangeHandler = onAssistantChange ? stableAssistantChange : undefined;
   const modelChangeHandler = onModelChange ? stableModelChange : undefined;
   const cancelStreamHandler = onCancelStream ? stableCancelStream : undefined;
   const stableDictation = dictation ? {...dictation, toggle: stableDictationToggle} : null;
   const stableUploadClick = useStableCallback(() => composer.handleUploadClick());
   const handleUploadExitComplete = useStableCallback(() => composer.setIsUploadButtonAnimating(false));
   const textarea: ComposerTextareaProps = {
      isSlashMenuOpen: slashMenu.isOpen,
      skillSlugs: isLoadingSkills ? [] : skills?.map((skill) => skill.slug),
      inputRef,
      value,
      onChange: slashMenu.handleChange,
      onSelect: slashMenu.handleSelect,
      onKeyDown: (event) => {
         if (!slashMenu.handleKeyDown(event)) composer.handleKeyDown(event);
      },
      onFocus: (event) => {
         composer.setIsFocused(true);
         slashMenu.handleFocus(event);
      },
      onBlur: () => {
         composer.setIsFocused(false);
         slashMenu.handleBlur();
      },
      placeholder,
      disabled,
      "aria-autocomplete": skills !== undefined || isLoadingSkills ? "list" : undefined,
      "aria-haspopup": skills !== undefined || isLoadingSkills ? "listbox" : undefined,
      "aria-controls": slashMenu.isOpen ? slashMenu.listboxId : undefined,
      "aria-expanded": skills !== undefined || isLoadingSkills ? slashMenu.isOpen : undefined,
      "aria-activedescendant": slashMenu.activeOptionId,
   };

   // Expose imperative controls so parents (e.g. skill cards on the welcome
   // screen) can drop a prompt into the box and immediately open the file
   // picker. openFilePicker honors the same gating as the upload button so it
   // can't pop a dialog when uploads are disabled/hidden. No deps array: each
   // render rebinds so the latest closures/props are used.
   useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      focusEnd: () => {
         const el = inputRef.current;
         if (!el) return;
         el.focus();
         const end = el.value.length;
         el.setSelectionRange(end, end);
      },
      openFilePicker: () => {
         if (disabled || uploadDisabled || !composer.shouldShowUploadButton) return;
         composer.handleUploadClick();
      },
   }));

   return (
      <form onSubmit={composer.handleFormSubmit} onPaste={composer.handlePaste} className={className}>
         <input
            ref={composer.fileInputRef}
            type="file"
            accept={fileUploadConfig?.acceptedFileTypes}
            multiple={(fileUploadConfig?.maxFileCount ?? 1) > 1}
            onChange={composer.handleFileInputChange}
            aria-label={ct("uploadFile")}
            title={ct("uploadFile")}
            className="hidden"
         />
         <Popover open={slashMenu.isOpen} onOpenChange={(open) => { if (!open) slashMenu.dismiss(); }}>
            <PopoverAnchor asChild>
               <div
                  ref={composer.containerRef}
                  className={clsx(
                     "relative overflow-hidden bg-background border cursor-text",
                     "[&_button]:cursor-pointer [&_a]:cursor-pointer [&_[role=combobox]]:cursor-pointer",
                     "transition-[border-color,box-shadow] duration-200",
                     "rounded-[20px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]",
                     variant === "large" && "min-h-[130px]",
                     composer.isFocused ? "border-neutral" : "border-neutral/60 hover:border-neutral"
                  )}
               >
                  <QueuedMessageBanner
                     message={composer.queuedMessage}
                     onClear={() => composer.setQueuedMessage(null)}
                  />

                  <FileAttachmentsList
                     files={attachedFiles}
                     scrollContainerRef={composer.scrollContainerRef}
                     onScroll={composer.updateScrollState}
                     scrollState={composer.scrollState}
                     incompatibleFiles={composer.incompatibleFiles}
                     onRemoveFile={composer.handleRemoveFile}
                  />

                  {variant === "compact" && (
                     <MobileCompactInputRow
                        maxRows={maxRows}
                        textarea={textarea}
                        upload={{
                           show: !!composer.shouldShowUploadButton,
                           onClick: composer.handleUploadClick,
                           disabled: uploadDisabled,
                           hasAttachedFiles: attachedFiles.length > 0,
                        }}
                        dictation={
                           composer.showDictationButton && dictation
                              ? {
                                   show: true,
                                   status: dictation.status,
                                   onToggle: stableDictationToggle,
                                   disabled: !!disabled,
                                }
                              : undefined
                        }
                        enableMemory={showMemory}
                        send={{
                           show: showSendButton,
                           isSubmitting,
                           onCancelStream: cancelStreamHandler,
                           canSubmit: composer.canSubmit,
                           loading,
                           tooltip: composer.sendTooltip,
                        }}
                     />
                  )}

                  <DesktopComposerLayout
                     variant={variant}
                     maxRows={maxRows}
                     onExpandedChange={onExpandedChange}
                     textarea={textarea}
                     upload={{
                        show: !!composer.shouldShowUploadButton,
                        animating: composer.isUploadButtonAnimating,
                        onExitComplete: handleUploadExitComplete,
                        onClick: stableUploadClick,
                        disabled: uploadDisabled,
                        hasAttachedFiles: attachedFiles.length > 0,
                     }}
                     tools={{
                        hasToolsContent: composer.hasToolsContent,
                        shouldShowContextWheel: composer.shouldShowContextWheel,
                        displayedTokenUsagePercent: composer.displayedTokenUsagePercent,
                        tokenLimitReached,
                        tokenBreakdown,
                        showAssistantSelector,
                        assistants,
                        selectedAssistant,
                        onAssistantChange: assistantChangeHandler,
                        assistantSelectorDisabled,
                        isLoadingAssistants,
                        showModelSelector: composer.showModelSelector,
                        onModelChange: modelChangeHandler,
                        canEditModel,
                        isModelSaving,
                        conversationId,
                     }}
                     dictation={composer.showDictationButton ? stableDictation : null}
                     enableMemory={showMemory}
                     send={{
                        show: showSendButton,
                        isSubmitting,
                        onCancelStream: cancelStreamHandler,
                        canSubmit: composer.canSubmit,
                        loading,
                        tooltip: composer.sendTooltip,
                     }}
                  />
               </div>
            </PopoverAnchor>
            {slashMenu.isOpen && (
               <SlashCommandMenu
                  menu={slashMenu}
                  isLoading={isLoadingSkills}
                  side={variant === "large" ? "bottom" : "top"}
                  containerRef={composer.containerRef}
               />
            )}
         </Popover>
      </form>
   );
});

ChatInputBase.displayName = "ChatInputBase";

export default ChatInputBase;
